import classnames from "classnames";
import { ElementType } from "react";
import {
  ResponsiveProp,
  normaliseResponsiveProp,
} from "../utils/responsiveProp";
import resetStyles from "./reset.module.scss";
import styles from "./useBoxStyles.module.scss";

export type Space =
  | "1"
  | "2"
  | "3"
  | "4" // 20px
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10"
  | "14" // 70px
  | "15"
  | "20"
  | "25"
  | "28"
  | "gutter"
  | "none";
export type ResponsiveSpace = ResponsiveProp<Space>;

export interface UseBoxStylesProps {
  component: ElementType | null;
  padding?: ResponsiveSpace;
  paddingX?: ResponsiveSpace;
  paddingY?: ResponsiveSpace;
  paddingTop?: ResponsiveSpace;
  paddingBottom?: ResponsiveSpace;
  paddingLeft?: ResponsiveSpace;
  paddingRight?: ResponsiveSpace;
  margin?: ResponsiveSpace;
  marginX?: ResponsiveSpace;
  marginY?: ResponsiveSpace;
  marginTop?: ResponsiveSpace;
  marginBottom?: ResponsiveSpace;
  marginLeft?: ResponsiveSpace;
  marginRight?: ResponsiveSpace;
  display?: ResponsiveProp<
    "none" | "flex" | "block" | "inline" | "inlineBlock"
  >;
  flexDirection?: ResponsiveProp<
    "row" | "column" | "rowReverse" | "columnReverse"
  >;
  flexWrap?: "wrap" | "nowrap";
  flexShrink?: 0;
  flexGrow?: 0 | 1;
  alignItems?: ResponsiveProp<"center" | "flexStart" | "flexEnd">;
  justifyContent?: ResponsiveProp<
    "center" | "flexStart" | "flexEnd" | "spaceBetween" | "spaceAround"
  >;
  textAlign?: ResponsiveProp<"left" | "right" | "center">;
  border?: ResponsiveProp<"none" | "standard" | "thick" | "dashed" | "xthick">;
  borderRadius?: "full" | "rounded" | "standard";
  borderColor?:
    | "primary"
    | "primary-highlight"
    | "accent"
    | "white"
    | "neutral-100"
    | "neutral-200"
    | "neutral-300"
    | "neutral-400"
    | "neutral-500"
    | "neutral-600"
    | "neutral-700"
    | "neutral-800"
    | "neutral-900"
    | "jets-black"
    | "jets-beige"
    | "success"
    | "warning"
    | "info"
    | "error";
  background?:
    | "primary"
    | "accent"
    | "white"
    | "neutral-100"
    | "neutral-200"
    | "neutral-300"
    | "neutral-400"
    | "neutral-500"
    | "neutral-600"
    | "neutral-700"
    | "neutral-800"
    | "neutral-900"
    | "jets-black"
    | "jets-beige"
    | "success"
    | "warning"
    | "info";
  boxShadow?: ResponsiveProp<
    "medium" | "small" | "large" | "stickyBottom" | "none"
  >; // Braid has many more, used for focus styles etc
  // transform?: keyof typeof styleRefs.transform;
  // transition?: keyof typeof styleRefs.transition;
  height?: "full" | "touchable";
  width?: "full" | "touchable";
  position?: ResponsiveProp<
    "absolute" | "relative" | "fixed" | "sticky" | "static"
  >;
  cursor?: "pointer" | "not-allowed";
  pointerEvents?: "none";
  overflow?: "hidden" | "auto" | "visible" | "scroll";
  maxWidth?: "medium" | "xsmall" | "small" | "large" | "xlarge";
  minWidth?: 0;
  userSelect?: "none";
  className?: Parameters<typeof classnames>[0];
}

export const useBoxStyles = ({
  component,
  padding,
  paddingX,
  paddingY,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  margin,
  marginX,
  marginY,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  display,
  flexDirection,
  flexWrap,
  flexShrink,
  flexGrow,
  alignItems,
  justifyContent,
  textAlign,
  border,
  borderColor,
  borderRadius,
  background,
  boxShadow,
  // transition,
  // transform,
  height,
  width,
  position,
  cursor,
  pointerEvents,
  overflow,
  maxWidth,
  minWidth,
  // userSelect,
  className,
}: UseBoxStylesProps): string => {
  const resolvedPaddingTop = paddingTop || paddingY || padding;
  const resolvedPaddingBottom = paddingBottom || paddingY || padding;
  const resolvedPaddingLeft = paddingLeft || paddingX || padding;
  const resolvedPaddingRight = paddingRight || paddingX || padding;

  const resolvedMarginTop = marginTop || marginY || margin;
  const resolvedMarginBottom = marginBottom || marginY || margin;
  const resolvedMarginLeft = marginLeft || marginX || margin;
  const resolvedMarginRight = marginRight || marginX || margin;

  return classnames(
    component !== null && component !== "div" && resetStyles.base,
    component !== null && resetStyles[component as string],
    getClasses("background", background),
    getResponsiveClasses("border", border),
    getClasses("border-color", borderColor),
    getClasses("border-radius", borderRadius),
    getResponsiveClasses("box-shadow", boxShadow),
    // getClasses("transition", transition),
    // getClasses("transform", transform),
    getClasses("height", height),
    getClasses("width", width),
    getClasses("cursor", cursor),
    getClasses("pointer-events", pointerEvents),
    getClasses("overflow", overflow),
    getClasses("max-width", maxWidth),
    getClasses("min-width", minWidth),
    getResponsiveClasses("position", position),
    getResponsiveClasses("margin-top", resolvedMarginTop),
    getResponsiveClasses("margin-bottom", resolvedMarginBottom),
    getResponsiveClasses("margin-left", resolvedMarginLeft),
    getResponsiveClasses("margin-right", resolvedMarginRight),
    getResponsiveClasses("padding-top", resolvedPaddingTop),
    getResponsiveClasses("padding-bottom", resolvedPaddingBottom),
    getResponsiveClasses("padding-left", resolvedPaddingLeft),
    getResponsiveClasses("padding-right", resolvedPaddingRight),
    getResponsiveClasses("display", display),
    getResponsiveClasses("flex-direction", flexDirection),
    getClasses("flex-wrap", flexWrap),
    getClasses("flex-shrink", flexShrink),
    getClasses("flex-grow", flexGrow),
    getResponsiveClasses("align-items", alignItems),
    getResponsiveClasses("justify-content", justifyContent),
    getResponsiveClasses("text-align", textAlign),
    // getClasses("user-select", userSelect),
    // getClasses("outline", outline),
    className,
  );
};

function getClasses(name: string, value?: string | number) {
  if (value === undefined) return null;

  if (!styles[`${name}_${value}`]) {
    console.warn("no style found for", `${name}_${value}`);
  }

  return styles[`${name}_${value}`];
}

function getResponsiveClasses<Keys extends string | number>(
  name: string,
  value?: ResponsiveProp<Keys>,
) {
  if (!value) return null;

  if (typeof value === "string" || typeof value === "number") {
    return styles[`${name}_${value}`];
  }

  const [mobileValue, tabletValue, desktopValue] =
    normaliseResponsiveProp(value);

  return [
    getClasses(name, mobileValue),
    getClasses(name + "_tablet", tabletValue),
    getClasses(name + "_desktop", desktopValue),
  ]
    .filter(Boolean)
    .join(" ");
}
